<template>
    <div id="printer-settings"
         class="modl-tab-body">
        <div class="modl-body">
            <div v-if="error"
                 data-testid="errorAlert"
                 class="alert alert-danger">
                {{ error }}
            </div>

            <div v-if="loading"
                 class="text-center">
                <PageLoadingSpinner />
            </div>
            <div v-else>
                <h4 class="modal__heading">
                    {{ l('PRINT_CONFIG.PRINT_ROUTING__HEADING') }}
                </h4>

                <div class="alert alert--small alert-info">
                    {{ l('PRINT_CONFIG.PRINT_ROUTING__INFO') }}
                </div>

                <table class="table table-striped text-left table--valign-middle">
                    <thead>
                        <tr>
                            <th>{{ l('PRINT_CONFIG.HEADING_TYPE') }}</th>
                            <th>{{ l('PRINT_CONFIG.HEADING_DRIVER') }}</th>
                            <th>{{ l('PRINT_CONFIG.HEADING_PRINTER') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <em>{{ l('PRINT_CONFIG.DEFAULT_ROW') }}</em>
                            </td>
                            <td>
                                <select v-model="defaultRouting.driverRef"
                                        class="form-control"
                                        data-testid="typeRouting-DEFAULT-driverRef"
                                        @change="cleanRouting(defaultRouting)">
                                    <!-- An option must be selected for the default routing. -->
                                    <option v-for="driverRef in printDriverRefs"
                                            :key="`default-print-ref-${driverRef}`"
                                            :value="driverRef">
                                        {{ driverRef }}
                                    </option>
                                </select>
                            </td>
                            <td>
                                <select v-model="defaultRouting.printerName"
                                        class="form-control"
                                        data-testid="typeRouting-DEFAULT-printer"
                                        :disabled="!canSelectPrinterForDriverRef(defaultRouting.driverRef)">
                                    <option :value="null"></option>
                                    <option v-for="printerName in printerNames"
                                            :key="`default-printer-option-${printerName}`"
                                            :value="printerName">
                                        {{ printerName }}
                                    </option>
                                </select>
                            </td>
                        </tr>
                        <tr v-for="type in printTypes"
                            :key="`print-type-row-${type}`">
                            <td>{{ type }}</td>
                            <td>
                                <select v-model="typeRoutings[type].driverRef"
                                        class="form-control"
                                        :data-testid="`typeRouting-${type}-driverRef`"
                                        @change="cleanRouting(typeRoutings[type])">
                                    <option :value="null"></option>
                                    <option v-for="driverRef in printDriverRefs"
                                            :key="`type-${type}-print-ref-${driverRef}`"
                                            :value="driverRef">
                                        {{ driverRef }}
                                    </option>
                                </select>
                            </td>
                            <td>
                                <select v-model="typeRoutings[type].printerName"
                                        class="form-control"
                                        :data-testid="`typeRouting-${type}-printer`"
                                        :disabled="typeRoutings[type] && !canSelectPrinterForDriverRef(typeRoutings[type].driverRef)">
                                    <option :value="null"></option>
                                    <option v-for="printerName in printerNames"
                                            :key="`type-${type}-printer-option-${printerName}`"
                                            :value="printerName">
                                        {{ printerName }}
                                    </option>
                                </select>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <h4 class="modal__heading">
                    {{ l('PRINT_CONFIG.CASH_DRAWER_PRINTER__HEADING') }}
                </h4>

                <div class="alert alert--small alert-info">
                    {{ l('PRINT_CONFIG.CASH_DRAWER_PRINTER__INFO') }}
                </div>

                <div class="form-h">
                    <div class="form-group">
                        <label class="control-label">
                            {{ l('PRINT_CONFIG.CASH_DRAWER_PRINTER__LABEL') }}
                        </label>
                        <div class="form-group__input">
                            <select v-model="autoOpenCashDrawerPrinter"
                                    :disabled="printerNames.length === 0"
                                    data-testid="CASH_DRAWER-printer"
                                    class="form-control input-lg">
                                <option :value="null"></option>
                                <option v-for="printerName in printerNames"
                                        :key="`printer-option-${printerName}`"
                                        :value="printerName">
                                    {{ printerName }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="modl-footer">
            <!-- Cancel button -->
            <button class="btn btn-lg btn-default cancel-btn"
                    @click.prevent="$emit('cancel')">
                {{ l('CANCEL') }}
            </button>

            <!-- OK button -->
            <button class="btn btn-lg btn-primary"
                    :disabled="saving"
                    data-testid="submit-button"
                    @click.prevent="submit">
                <i v-if="saving"
                   class="fa-solid fa-spinner fa-spin" />
                <template v-else>
                    {{ l('SAVE') }}
                </template>
            </button>
        </div>
    </div>
</template>

<script>
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PrintDriverRefs from '@/lib/Printing/PrintDriverRefs';
import PrintRouting from '@/lib/Printing/PrintRouting';
import PrintType from '@/lib/Printing/PrintType';
import { cloneDeep } from '@/functions/clone';
import { container } from '@/container';

export default {
    name: 'PrintSettingsTab',

    components: { PageLoadingSpinner },

    emits: [
        'cancel',
        'resolve',
    ],

    data() {
        return {
            // Current and desired values for the print settings.
            defaultRouting: null,
            typeRoutings: null,

            /**
             * List of all printer names for the selects.
             *
             * @type {string[]}
             */
            printerNames: [],

            /**
             * List of possible print drivers for the selects.
             *
             * @type {string[]}
             */
            printDriverRefs: [
                PrintDriverRefs.PRINT,
                PrintDriverRefs.PRINTER_APP, // Also used for Agent V3
            ],

            /**
             * List of all the types of things that can be printed.
             *
             * @type {string[]}
             */
            printTypes: Object.values(PrintType),

            // Properties of the modal.
            error: null,
            loading: true,
            saving: false,
            /**
             * The name of the printer to which the cash drawer is attached if we want to open the cash drawer without
             * printing.
             *
             * @type {string|null}
             */
            autoOpenCashDrawerPrinter: null,
        };
    },

    created() {
        /**
         * @type {PrintRouting}
         */
        this.defaultRouting = cloneDeep(container.printRouter.defaultRouting);

        // Load all the specific type routings.
        this.typeRoutings = this.loadTypeRoutings();

        // Load available printers.
        this.loadAvailablePrinterNames()
            .finally(() => {
                this.loading = false;
            });

        // Clean the exiting settings.
        // - Remove invalid print methods.
        // - Remove a selected printer if that print method does not allow specifying a printer.
        this.cleanRouting(this.defaultRouting);
        Object.values(this.typeRoutings).forEach((typeRouting) => {
            this.cleanRouting(typeRouting);
        });

        this.autoOpenCashDrawerPrinter = container.cashDrawerService.getPrinterName();
    },

    methods: {
        /**
         * Load the existing print routings set on the device.
         *
         * @return {Object.<string, PrintRouting>}
         */
        loadTypeRoutings() {
            // Load the existing routings. These will only include ones that have been defined.
            const typeRoutings = cloneDeep(container.printRouter.typeRoutings);

            // We need a routing to exist for every possible type so that the template does not cause errors.
            // So fill in the missing types.
            Object.keys(PrintType)
                .forEach((printType) => {
                    if (!typeRoutings.hasOwnProperty(printType)) {
                        typeRoutings[printType] = new PrintRouting();
                    }
                });

            return typeRoutings;
        },

        /**
         * Use the ProPoint Printer app (or Agent V3) to get a list of printers that exist on the system.
         * Set them as this.printerNames.
         *
         * @return {Promise<string[]>}
         */
        async loadAvailablePrinterNames() {
            let printerNames = [];
            try {
                this.printerNames = await container.availablePrintersManager.getPrinterNames();
            } catch (e) {
                this.error = e.message;
            }

            return printerNames;
        },

        /**
         * @param {string} driverRef
         *
         * @return {boolean}
         */
        canSelectPrinterForDriverRef(driverRef) {
            return container.printRouter.canSelectPrinterForDriverRef(driverRef);
        },

        cleanRouting(routing) {
            // Remove an invalid driver ref.
            if (routing && !this.printDriverRefs.includes(routing.driverRef)) {
                routing.driverRef = null;
            }

            // Clear printer names for print methods that don't allow selecting a printer.
            if (routing && !this.canSelectPrinterForDriverRef(routing.driverRef)) {
                routing.printerName = null;
            }
        },

        submit() {
            if (this.saving) {
                return;
            }

            this.error = null;
            this.saving = true;

            if (!this.defaultRouting.driverRef) {
                this.error = this.l('PRINT_CONFIG.ERROR_NO_DEFAULT_DRIVER');
                return;
            }

            // First set the new settings into the PrintRouter.
            container.printRouter.setDefaultRouting(this.defaultRouting);
            container.printRouter.setTypeRoutings(this.typeRoutings);

            let printConfig = JSON.parse(container.currentDevice.device.printConfig);
            if (!printConfig) {
                printConfig = {};
            }

            // Set known properties and leave everything else.
            printConfig.routing = container.printRouter.exportConfig();
            printConfig.autoOpenCashDrawerPrinter = this.autoOpenCashDrawerPrinter;

            // Set these settings onto the current device.
            container.currentDevice.updatePrintConfigForCurrentDevice(printConfig)
                .then(() => {
                    this.$emit('resolve');
                })
                .catch((e) => {
                    this.error = e.message;
                })
                .finally(() => {
                    this.saving = false;
                });

            // Update or set the value on the cashDrawerService.
            container.cashDrawerService.setPrinterName(this.autoOpenCashDrawerPrinter);
        },
    },
};
</script>
